<template>
  <entity-list-layout
    class="admin-account-list"
    title="ACCOUNT"
    :count="accounts.length"
    :query.sync="search"
    :search-hint="$t('label.search')"
  >
    <template #title-area-content>
      <managed-by-select
        v-if="showsManagedBy"
        v-model="managedBy"
        class="admin-account-list__managed-by-select"
        :distributors="distributors"
      />
      <create-account-button />
    </template>
    <div class="admin-account-list__filters">
      <v-checkbox
        v-model="deactivated"
        :disabled="blocked"
        dense
        hide-details
        :label="$t('label.deactivated_users_only')"
      />
      <v-checkbox
        v-model="blocked"
        :disabled="deactivated"
        dense
        hide-details
        :label="$t('label.blocked_users_only')"
      />
    </div>
    <p-data-list :data="accounts" :pagination.sync="pagination">
      <data-field name="No" headerClass="column--no">
        <template v-slot="props">{{ props.index + 1 }}</template>
      </data-field>
      <data-field :name="$t('field.organization')" value="organization.name" sortable>
        <template v-slot="props">
          <router-link :to="{ name: 'admin-organization', params: { id: props.item.organization.id }}">
            {{ props.item.organization.name }}
          </router-link>
        </template>
      </data-field>
      <data-field :name="$t('field.name')" value="name" headerClass="column--name" sortable>
        <template v-slot="props">
          <router-link :to="{ name: 'admin-account', params: { email: props.item.email }}">
            {{ props.item.name }}
          </router-link>
          <i
            class="admin-account-list__deactived-label"
            v-if="!props.item.activated"
          >({{$t( 'label.deactivated') }})</i>
          <i class="admin-account-list__blocked-label" v-if="props.item.blocked">({{$t( 'label.blocked') }})</i>
          <i v-if="props.item.distributor">({{ $t('label.distributor') }})</i>
        </template>
      </data-field>
      <data-field :name="$t('field.email')" value="email" headerClass="column--email" sortable>
        <template v-slot="props">{{ props.item.email }}</template>
      </data-field>
      <data-field :name="$t('field.invited_by')" value="invitedBy" headerClass="column--invited_by" sortable>
        <template v-slot="props">
          {{ props.item.invitedBy }}
        </template>
      </data-field>
      <data-field
        :name="$t('field.otp')"
        value="use_otp"
        headerClass="column--otp"
        sortable
      >
        <template v-slot="props">
          <v-icon v-if="props.item.use_otp" color="success" small>
            fa-check-circle
          </v-icon>
          <v-icon v-else color="grey" small>
            fa-minus-circle
          </v-icon>
        </template>
      </data-field>
      <data-field
        v-if="showsManagedBy"
        :name="$t('field.managed_by')"
        value="managedBySortKey"
        headerClass="column--managed-by"
        sortable
      >
        <template v-slot="props">
          <span
            v-if="props.item.managedByName"
            class="admin-account-list__managed-by"
            :class="{ 'text--disabled': props.item.managedByIsDirect }"
          >{{ props.item.managedByName }}</span>
          <span v-else data-testid="managed-by-empty">--</span>
        </template>
      </data-field>
    </p-data-list>
  </entity-list-layout>
</template>

<i18n lang="yaml">
ja:
  label:
    search: '文字列で検索'
    deactivated: '無効'
    blocked: 'ブロック'
    distributor: '代理店'
    deactivated_users_only: '無効なユーザーのみ'
    blocked_users_only: 'ブロックされたユーザーのみ'

  field:
    name: 'ユーザー名'
    email: 'E-mail'
    organization: '組織名'
    invited_by: '共有元'
    otp: 'OTP'
    managed_by: '管理元'

  messages:
    failed_to_load: 'アカウント一覧の読み込みに失敗しました'

en:
  label:
    search: 'Search by text'
    deactivated: 'deactivated'
    blocked: 'blocked'
    distributor: 'distributor'
    deactivated_users_only: 'Deactivated Users only'
    blocked_users_only: 'Blocked users only'

  field:
    name: 'Name'
    email: 'E-mail'
    organization: 'Organization'
    invited_by: 'Invited by'
    otp: 'OTP'
    managed_by: 'Managed by'

  messages:
    failed_to_load: 'Failed to load accounts'
</i18n>

<script>
import Features from '@/mixins/features';
import Notifications from '@/mixins/notifications';

import EntityListLayout from '@/components/atoms/EntityListLayout';
import ManagedBySelect from '@/components/molecules/ManagedBySelect';
import {
  describeManagedBy, directLabel, extractDistributors, matchesManagedBy,
} from '@/libs/managedBy';
import DataList, { Field } from '../DataList';
import CreateAccountButton from './CreateAccountButton';

export default {
  name: 'admin-account-list',
  mixins: [
    Features,
    Notifications,
  ],
  created() {
    this.loadUsers();
  },
  components: {
    CreateAccountButton,
    DataField: Field,
    EntityListLayout,
    ManagedBySelect,
    PDataList: DataList,
  },
  computed: {
    // ログイン中のユーザー情報は取得のたびに差し替わるため、同一ユーザーかどうかはメールアドレスで判定する
    currentUserEmail() {
      return this.$store.getters.getCurrentUser?.email;
    },
    // 管理元（選択欄・列・検索対象）は admin だけに見せる。代理店ログイン・代理ログイン中は admin が無く、
    // API も管理元を返さない。ログアウト直後は getCurrentUser が空になるため ?. で守る
    showsManagedBy() {
      return Boolean(this.$store.getters.getCurrentUser?.admin) && this.featureIsActive('distributor_account');
    },
    distributors() {
      return extractDistributors(this.$store.getters.getUsers.valuesArray());
    },
    accounts() {
      const accounts = this.$store.getters.getUsers;
      return accounts.map((account) => {
        const organization = account.organization || {};
        return {
          ...account,
          organization,
          invitedBy: this.invitedByToString(account.invited_by),
          ...this.managedByFields(account),
        };
      }).filter((account) => {
        if ((this.deactivated && account.activated) ||
          (this.blocked && !account.blocked)) {
          return false;
        }
        if (this.showsManagedBy && !matchesManagedBy(account.managing_distributor_id, this.managedBy)) {
          return false;
        }
        return this.userContains(this.search.toLowerCase(), account);
      });
    },
  },
  data() {
    return {
      pagination: {
        itemsPerPage: 20,
      },
      search: '',
      blocked: false,
      deactivated: false,
      managedBy: null,
    };
  },
  watch: {
    // 代理ログインの開始・解除でログイン中のユーザーが切り替わると一覧が消去されるため、取り直す
    currentUserEmail(email) {
      // ログアウト直後はユーザー情報が空になる。未ログインで取得しないようにする
      if (email != null) {
        this.loadUsers();
      }
    },
  },
  methods: {
    // 代理店一覧もこの取得結果から作るため、管理元のために別の取得はしない
    async loadUsers() {
      try {
        await this.$store.dispatch('requestUsers');
      } catch (error) {
        // 画面遷移・代理ログインの切替・ログアウトで中断された取得（キャンセル）は通知しない
        if (this.$http.isCancel(error)) return;
        this.notifyError('messages.failed_to_load');
      }
    },
    managedByFields(account) {
      if (!this.showsManagedBy) return {};
      return describeManagedBy(account.managing_distributor_id, this.distributors, directLabel(this.$i18n.locale));
    },
    userContains(keywords, user) {
      const organization = user.organization || {};
      const organizationName = (organization.name || '');

      // eslint-disable-next-line arrow-body-style
      return this._.every(keywords.split(' '), (keyword) => {
        return this._.some([
          user.name,
          user.email,
          organizationName,
          user.invitedBy,
          // 下の toLowerCase は null を受けられないため、管理元名が無いときは空文字にする
          user.managedByName || '',
        ], (value) => {
          const lowerValue = value.toLowerCase();
          return this._.includes(lowerValue, keyword.toLowerCase());
        });
      });
    },
    invitedByToString(invitedBy) {
      if (invitedBy) {
        const { name, organization_name: organizationName } = invitedBy;
        return `${organizationName || ''} (${name})`;
      }
      return '';
    },
  },
};
</script>

<style scoped lang="sass">
@import 'vuetify/src/styles/styles.sass'

.admin-account-list
  &__deactivated-label
    color: map-get($material-theme, 'text' ,'disabled')

  &__blocked-label
    color: map-get($colors, 'red', 'base')

  &__managed-by-select
    // 長い代理店名で幅が伸びて 2 段にならないよう固定し、選択中の名前は省略表示する。
    // スマホ表示では検索欄と同じく横幅いっぱいに広げる
    @media #{map-get($display-breakpoints, 'md-and-up')}
      width: 220px

  &__managed-by
    overflow-wrap: anywhere

  // タイトル行に収まらない絞り込みのチェックボックスを、一覧の上に 1 行で並べる
  &__filters
    display: flex
    flex-wrap: wrap
    gap: 0 24px
    margin-bottom: 8px

    ::v-deep .v-input--checkbox
      margin-top: 0
      padding-top: 0
      color: map-get($material-theme, 'text', 'primary')

    ::v-deep .v-label
      font-size: 0.875rem

  ::v-deep .column
    &--no
      width: 48px

    &--name,
    &--email
      width: 25%

    &--invited_by
      width: 120px

    &--otp
      width: 72px
      text-align: center

    &--managed-by
      width: 140px
</style>
